/**
 * RadioStationsTab — Admin interface for managing curated HBCU radio stations.
 *
 * Features:
 *  - List all stations (active + inactive) with status, format, HTTPS badge
 *  - Inline test-stream button (plays 8 seconds of audio)
 *  - Add / Edit (modal form) / Delete
 *  - Active toggle
 *  - Seed defaults button (safe to run multiple times)
 *  - Color-coded: green = active HTTPS, amber = active HTTP, gray = inactive
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { adminAPI } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import {
  Radio, Plus, Pencil, Trash2, Play, Pause, Square,
  CheckCircle, XCircle, AlertTriangle, RefreshCw,
  ExternalLink, Loader2, Globe, Lock, Unlock,
} from 'lucide-react';

// ── Helpers ───────────────────────────────────────────────────────────────────

const isHttps = (url) => (url || '').startsWith('https://');
const hasUrl  = (url) => !!(url || '').trim();

function StatusBadge({ station }) {
  if (!station.active) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-slate-100 text-slate-500">
        <XCircle className="w-3 h-3" /> Inactive
      </span>
    );
  }
  if (!hasUrl(station.stream_url)) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-700">
        <AlertTriangle className="w-3 h-3" /> No URL
      </span>
    );
  }
  if (!isHttps(station.stream_url)) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-700">
        <Unlock className="w-3 h-3" /> HTTP only
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-100 text-emerald-700">
      <CheckCircle className="w-3 h-3" /> Live · HTTPS
    </span>
  );
}

// ── Empty station form ────────────────────────────────────────────────────────
const EMPTY = {
  callsign: '', name: '', frequency: '', college: '', college_short: '',
  city: '', state: '', genre: '', stream_url: '', stream_format: 'mp3',
  website: '', logo_url: '', active: true, notes: '',
};

// ── Station form modal ────────────────────────────────────────────────────────

function StationForm({ initial, onSave, onCancel, saving }) {
  const [form, setForm] = useState(initial || EMPTY);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.4)' }}
    >
      <div
        className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl shadow-2xl p-6"
        style={{ backgroundColor: 'hsl(var(--card))' }}
      >
        <h3 className="font-heading font-bold text-dark-slate text-lg mb-4">
          {initial ? 'Edit Station' : 'Add Station'}
        </h3>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-medium text-slate-gray mb-1 block">Call Sign *</label>
            <Input value={form.callsign} onChange={e => set('callsign', e.target.value.toUpperCase())} placeholder="WHUR" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-gray mb-1 block">Frequency</label>
            <Input value={form.frequency} onChange={e => set('frequency', e.target.value)} placeholder="96.3 FM" />
          </div>
          <div className="col-span-2">
            <label className="text-xs font-medium text-slate-gray mb-1 block">Station Name *</label>
            <Input value={form.name} onChange={e => set('name', e.target.value)} placeholder="WHUR 96.3 FM" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-gray mb-1 block">College / University</label>
            <Input value={form.college} onChange={e => set('college', e.target.value)} placeholder="Howard University" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-gray mb-1 block">Short Name (for badge)</label>
            <Input value={form.college_short} onChange={e => set('college_short', e.target.value)} placeholder="Howard" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-gray mb-1 block">City</label>
            <Input value={form.city} onChange={e => set('city', e.target.value)} placeholder="Washington" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-gray mb-1 block">State</label>
            <Input value={form.state} onChange={e => set('state', e.target.value.toUpperCase())} placeholder="DC" maxLength={2} />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-gray mb-1 block">Genre</label>
            <Input value={form.genre} onChange={e => set('genre', e.target.value)} placeholder="Jazz / Urban AC" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-gray mb-1 block">Format</label>
            <select
              value={form.stream_format}
              onChange={e => set('stream_format', e.target.value)}
              className="w-full h-9 rounded-md border border-slate-200 px-3 text-sm bg-white"
            >
              <option value="mp3">MP3</option>
              <option value="aac">AAC</option>
              <option value="hls">HLS (.m3u8)</option>
            </select>
          </div>
          <div className="col-span-2">
            <label className="text-xs font-medium text-slate-gray mb-1 block">
              Stream URL
              {form.stream_url && !isHttps(form.stream_url) && (
                <span className="ml-2 text-amber-600 font-normal">⚠ HTTP-only — may not play on HTTPS sites</span>
              )}
              {form.stream_url && isHttps(form.stream_url) && (
                <span className="ml-2 text-emerald-600 font-normal">✓ HTTPS</span>
              )}
            </label>
            <Input
              value={form.stream_url}
              onChange={e => set('stream_url', e.target.value)}
              placeholder="https://stream.example.com/live"
              className={form.stream_url && !isHttps(form.stream_url) ? 'border-amber-300' : ''}
            />
          </div>
          <div className="col-span-2">
            <label className="text-xs font-medium text-slate-gray mb-1 block">Website</label>
            <Input value={form.website} onChange={e => set('website', e.target.value)} placeholder="https://whur.com/" />
          </div>
          <div className="col-span-2">
            <label className="text-xs font-medium text-slate-gray mb-1 block">Logo URL (optional)</label>
            <Input value={form.logo_url} onChange={e => set('logo_url', e.target.value)} placeholder="https://…/logo.png" />
          </div>
          <div className="col-span-2">
            <label className="text-xs font-medium text-slate-gray mb-1 block">Notes</label>
            <textarea
              value={form.notes}
              onChange={e => set('notes', e.target.value)}
              rows={2}
              placeholder="Stream URL source, contact info, known issues…"
              className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm resize-none"
            />
          </div>
          <div className="col-span-2 flex items-center gap-2">
            <input
              type="checkbox"
              id="active-check"
              checked={form.active}
              onChange={e => set('active', e.target.checked)}
              className="w-4 h-4 rounded accent-emerald-500"
            />
            <label htmlFor="active-check" className="text-sm text-dark-slate">
              Active (show on HBCU Media page)
            </label>
          </div>
        </div>

        <div className="flex gap-2 mt-5 justify-end">
          <Button variant="outline" onClick={onCancel} disabled={saving}>Cancel</Button>
          <Button
            onClick={() => onSave(form)}
            disabled={saving || !form.callsign || !form.name}
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
            {initial ? 'Save changes' : 'Add station'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── Main tab component ────────────────────────────────────────────────────────

export default function RadioStationsTab() {
  const [stations,   setStations]   = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [formOpen,   setFormOpen]   = useState(false);
  const [editTarget, setEditTarget] = useState(null); // station being edited
  const [saving,     setSaving]     = useState(false);
  const [seeding,    setSeeding]    = useState(false);
  const [filter,     setFilter]     = useState('all'); // 'all'|'active'|'inactive'|'no-url'

  // Audio test state
  const audioRef     = useRef(null);
  const [testingId,  setTestingId]  = useState(null);  // station id currently being tested
  const [testPlaying, setTestPlaying] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminAPI.radioStations();
      setStations(res.data || []);
    } catch {
      toast.error('Could not load stations');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // ── Audio test ─────────────────────────────────────────────────────────────
  const testStream = (station) => {
    // Stop any existing test
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = '';
    }
    if (testingId === station.id && testPlaying) {
      setTestingId(null);
      setTestPlaying(false);
      return;
    }
    if (!station.stream_url) {
      toast.error('No stream URL to test');
      return;
    }
    const audio = new Audio(station.stream_url);
    audioRef.current = audio;
    audio.volume = 0.5;
    audio.play()
      .then(() => {
        setTestingId(station.id);
        setTestPlaying(true);
        // Auto-stop after 10s
        setTimeout(() => {
          audio.pause();
          audio.src = '';
          setTestingId(null);
          setTestPlaying(false);
        }, 10000);
      })
      .catch(() => toast.error('Could not play stream — it may be down or HTTP-only'));

    audio.onerror = () => {
      setTestingId(null);
      setTestPlaying(false);
      toast.error('Stream error');
    };
  };

  // Cleanup on unmount
  useEffect(() => () => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current.src = ''; }
  }, []);

  // ── CRUD ───────────────────────────────────────────────────────────────────
  const handleSave = async (form) => {
    setSaving(true);
    try {
      if (editTarget) {
        await adminAPI.updateRadioStation(editTarget.id, form);
        toast.success('Station updated');
      } else {
        await adminAPI.createRadioStation(form);
        toast.success('Station added');
      }
      setFormOpen(false);
      setEditTarget(null);
      await load();
    } catch {
      toast.error('Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (station) => {
    try {
      await adminAPI.updateRadioStation(station.id, { ...station, active: !station.active });
      setStations(prev => prev.map(s => s.id === station.id ? { ...s, active: !s.active } : s));
    } catch {
      toast.error('Update failed');
    }
  };

  const handleDelete = async (station) => {
    if (!window.confirm(`Delete ${station.name}?`)) return;
    try {
      await adminAPI.deleteRadioStation(station.id);
      setStations(prev => prev.filter(s => s.id !== station.id));
      toast.success('Station deleted');
    } catch {
      toast.error('Delete failed');
    }
  };

  const handleSeed = async () => {
    setSeeding(true);
    try {
      const res = await adminAPI.seedRadioStations();
      const { inserted, skipped } = res.data;
      toast.success(`Seeded ${inserted} stations (${skipped} already existed)`);
      await load();
    } catch {
      toast.error('Seed failed');
    } finally {
      setSeeding(false);
    }
  };

  // ── Filtered list ──────────────────────────────────────────────────────────
  const displayed = stations.filter(s => {
    if (filter === 'active')   return s.active && hasUrl(s.stream_url);
    if (filter === 'inactive') return !s.active;
    if (filter === 'no-url')   return !hasUrl(s.stream_url);
    return true;
  });

  // ── Stats ──────────────────────────────────────────────────────────────────
  const total      = stations.length;
  const activeOk   = stations.filter(s => s.active && hasUrl(s.stream_url) && isHttps(s.stream_url)).length;
  const httpOnly   = stations.filter(s => hasUrl(s.stream_url) && !isHttps(s.stream_url)).length;
  const noUrl      = stations.filter(s => !hasUrl(s.stream_url)).length;

  return (
    <div className="space-y-5">
      {/* ── Stats bar ── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Total stations', value: total,    color: 'text-dark-slate' },
          { label: 'Live (HTTPS)',   value: activeOk, color: 'text-emerald-600' },
          { label: 'HTTP only',      value: httpOnly, color: 'text-amber-600' },
          { label: 'No URL yet',     value: noUrl,    color: 'text-slate-400' },
        ].map(stat => (
          <div key={stat.label} className="p-4 rounded-2xl" style={{ backgroundColor: 'hsl(var(--secondary))' }}>
            <p className={`text-2xl font-bold ${stat.color}`}>{stat.value}</p>
            <p className="text-xs text-slate-gray mt-0.5">{stat.label}</p>
          </div>
        ))}
      </div>

      {/* ── Toolbar ── */}
      <div className="flex flex-wrap gap-2 items-center justify-between">
        <div className="flex gap-2 flex-wrap">
          {/* Filter pills */}
          {[
            { id: 'all',      label: 'All' },
            { id: 'active',   label: 'Live' },
            { id: 'inactive', label: 'Inactive' },
            { id: 'no-url',   label: 'No URL' },
          ].map(f => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                filter === f.id
                  ? 'bg-dark-slate text-white'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load} disabled={loading}>
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={handleSeed} disabled={seeding}>
            {seeding ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Radio className="w-3.5 h-3.5 mr-1.5" />}
            Seed defaults
          </Button>
          <Button size="sm" onClick={() => { setEditTarget(null); setFormOpen(true); }}>
            <Plus className="w-3.5 h-3.5 mr-1.5" />
            Add station
          </Button>
        </div>
      </div>

      {/* ── No-URL tip ── */}
      {noUrl > 0 && (
        <div className="px-4 py-3 rounded-xl bg-amber-50 border border-amber-200 text-sm text-amber-800 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>
            <strong>{noUrl} station{noUrl > 1 ? 's' : ''}</strong> have no stream URL yet.
            Visit each station's website and use browser DevTools (Network tab, filter by audio) to find the direct stream endpoint.
          </span>
        </div>
      )}

      {/* ── Station table ── */}
      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="w-7 h-7 text-slate-300 animate-spin" />
        </div>
      ) : displayed.length === 0 ? (
        <div className="text-center py-16 text-slate-gray">
          No stations match this filter.
        </div>
      ) : (
        <div className="rounded-2xl overflow-hidden border" style={{ borderColor: 'hsl(var(--border))' }}>
          <table className="w-full text-sm">
            <thead style={{ backgroundColor: 'hsl(var(--secondary))' }}>
              <tr>
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-gray">Station</th>
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-gray hidden md:table-cell">College</th>
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-gray hidden lg:table-cell">Genre</th>
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-gray">Status</th>
                <th className="text-right px-4 py-2.5 text-xs font-semibold text-slate-gray">Actions</th>
              </tr>
            </thead>
            <tbody>
              {displayed.map((station, i) => (
                <tr
                  key={station.id}
                  className="border-t transition-colors hover:bg-slate-50/50"
                  style={{ borderColor: 'hsl(var(--border))' }}
                >
                  {/* Station name + callsign + URL preview */}
                  <td className="px-4 py-3">
                    <div className="flex items-start gap-2.5">
                      {/* Logo or callsign pill */}
                      {station.logo_url ? (
                        <img src={station.logo_url} alt="" className="w-8 h-8 rounded-lg object-cover flex-shrink-0 mt-0.5" />
                      ) : (
                        <div className="w-8 h-8 rounded-lg bg-dark-slate flex items-center justify-center flex-shrink-0 mt-0.5">
                          <span className="text-[9px] font-bold text-mint-fresh leading-tight text-center px-0.5">
                            {station.callsign.slice(0, 4)}
                          </span>
                        </div>
                      )}
                      <div className="min-w-0">
                        <p className="font-semibold text-dark-slate leading-tight">{station.name}</p>
                        <p className="text-xs text-slate-gray">{station.city}{station.state ? `, ${station.state}` : ''}</p>
                        {hasUrl(station.stream_url) && (
                          <p className="text-[10px] text-slate-400 truncate max-w-[200px] mt-0.5 font-mono">
                            {station.stream_url}
                          </p>
                        )}
                      </div>
                    </div>
                  </td>

                  {/* College */}
                  <td className="px-4 py-3 hidden md:table-cell">
                    <p className="text-dark-slate">{station.college_short || station.college || '—'}</p>
                    {station.frequency && <p className="text-xs text-slate-gray">{station.frequency}</p>}
                  </td>

                  {/* Genre */}
                  <td className="px-4 py-3 hidden lg:table-cell">
                    <p className="text-slate-gray text-xs">{station.genre || '—'}</p>
                  </td>

                  {/* Status */}
                  <td className="px-4 py-3">
                    <StatusBadge station={station} />
                  </td>

                  {/* Actions */}
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      {/* Test stream */}
                      {hasUrl(station.stream_url) && (
                        <button
                          onClick={() => testStream(station)}
                          title={testingId === station.id && testPlaying ? 'Stop test' : 'Test stream (10s)'}
                          className={`w-7 h-7 rounded-lg flex items-center justify-center transition-colors ${
                            testingId === station.id && testPlaying
                              ? 'bg-red-100 text-red-600'
                              : 'hover:bg-emerald-100 text-slate-gray hover:text-emerald-700'
                          }`}
                        >
                          {testingId === station.id && testPlaying
                            ? <Square className="w-3.5 h-3.5" />
                            : <Play   className="w-3.5 h-3.5" />}
                        </button>
                      )}

                      {/* Toggle active */}
                      <button
                        onClick={() => handleToggleActive(station)}
                        title={station.active ? 'Deactivate' : 'Activate'}
                        className="w-7 h-7 rounded-lg flex items-center justify-center hover:bg-slate-100 text-slate-gray hover:text-dark-slate transition-colors"
                      >
                        {station.active
                          ? <CheckCircle className="w-3.5 h-3.5 text-emerald-500" />
                          : <XCircle    className="w-3.5 h-3.5 text-slate-400" />}
                      </button>

                      {/* Website link */}
                      {station.website && (
                        <a
                          href={station.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          title="Station website"
                          className="w-7 h-7 rounded-lg flex items-center justify-center hover:bg-slate-100 text-slate-gray hover:text-dark-slate transition-colors"
                        >
                          <ExternalLink className="w-3.5 h-3.5" />
                        </a>
                      )}

                      {/* Edit */}
                      <button
                        onClick={() => { setEditTarget(station); setFormOpen(true); }}
                        title="Edit"
                        className="w-7 h-7 rounded-lg flex items-center justify-center hover:bg-slate-100 text-slate-gray hover:text-dark-slate transition-colors"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>

                      {/* Delete */}
                      <button
                        onClick={() => handleDelete(station)}
                        title="Delete"
                        className="w-7 h-7 rounded-lg flex items-center justify-center hover:bg-red-100 text-slate-gray hover:text-red-600 transition-colors"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Form modal ── */}
      {formOpen && (
        <StationForm
          initial={editTarget}
          onSave={handleSave}
          onCancel={() => { setFormOpen(false); setEditTarget(null); }}
          saving={saving}
        />
      )}
    </div>
  );
}
